// apps/web/src/features/crm/linkedRequests.ts — TanStack Query hooks for
// the linked-request reverse-read endpoints (#1596) consumed by the lead
// and deal detail drawers (#1598).
//
// Web-only (react-query + the apps/web api wrappers): the fetch goes
// through the web `request()` client, and these rows feed a cross-route
// navigation to the China request workspace, so they do NOT belong in the
// cross-runtime `@magimp/shared-hooks` package.
import { useQuery } from '@tanstack/react-query';
import { leadsKeys } from '@magimp/shared-hooks';
import type { LinkedRequestListResponse } from '@magimp/shared';
import { fetchDealRequests, fetchLeadRequests } from '../../api/crm/linked-requests.js';
import { dealsKeys } from './deals/queries.js';

/**
 * @aiPurpose linked-request rows for a Lead (#1598). Keyed under the lead's
 *   detail prefix (`leadsKeys.requests`) so a prefix invalidation of the
 *   lead flushes these rows. `enabled` gates on a non-null id.
 */
export function useLeadRequests(leadId: string | null) {
  return useQuery<LinkedRequestListResponse>({
    queryKey: leadsKeys.requests(leadId ?? ''),
    enabled: !!leadId,
    queryFn: () => fetchLeadRequests(leadId as string),
  });
}

/**
 * @aiPurpose linked-request rows for a Deal (#1598). Mirrors
 *   `useLeadRequests`, keyed under `dealsKeys.requests`.
 */
export function useDealRequests(dealId: string | null) {
  return useQuery<LinkedRequestListResponse>({
    queryKey: dealsKeys.requests(dealId ?? ''),
    enabled: !!dealId,
    queryFn: () => fetchDealRequests(dealId as string),
  });
}
